import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  CaretLeft,
  Clock,
  Check,
  X,
  CookingPot,
  Package,
  Bell,
  Printer,
  User,
  Phone,
  MapPin,
  Timer,
  Coffee,
  Leaf,
  QrCode,
  Money,
  CreditCard,
  SpeakerHigh,
  SpeakerSlash,
  Gear,
  BluetoothConnected
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════
// KITCHEN DASHBOARD - Restaurant Order Management
// ═══════════════════════════════════════════════════════════════════════════

const COLORS = {
  bg: "#F5E6E0",
  green: "#2D5A3D",
  orange: "#E07A3D",
  tan: "#D4A574",
  red: "#DC3545",
  text: "#1A1A1A",
  textMuted: "#6B5B4F",
  white: "#FFFFFF",
};

const STATUS_CONFIG = {
  pending: { label: "Neu", color: COLORS.orange, icon: Bell },
  confirmed: { label: "Bestätigt", color: COLORS.green, icon: Check },
  preparing: { label: "In Zubereitung", color: COLORS.tan, icon: CookingPot },
  ready: { label: "Fertig", color: COLORS.green, icon: Package },
};

// ═══════════════════════════════════════════════════════════════════════════
// ORDER CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const OrderCard = ({ order, onAccept, onPreparing, onReady, onCancel, onPrint }) => {
  const [estimatedTime, setEstimatedTime] = useState(30);
  const [showTimeSelector, setShowTimeSelector] = useState(false);
  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  };

  const getTimeAgo = (isoString) => {
    if (!isoString) return "";
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Gerade eben";
    if (mins < 60) return `vor ${mins} Min.`;
    return `vor ${Math.floor(mins / 60)} Std.`;
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className="rounded-2xl overflow-hidden"
      style={{ backgroundColor: COLORS.white }}
    >
      {/* Header */}
      <div 
        className="p-4 flex items-center justify-between"
        style={{ backgroundColor: statusConfig.color + "15" }}
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: statusConfig.color }}
          >
            <StatusIcon size={20} weight="fill" className="text-white" />
          </div>
          <div>
            <p className="font-black text-lg" style={{ color: COLORS.text }}>
              #{order.order_number}
            </p>
            <p className="text-sm" style={{ color: COLORS.textMuted }}>
              {formatTime(order.created_at)} · {getTimeAgo(order.created_at)}
            </p>
          </div>
        </div>
        
        <span 
          className="px-3 py-1.5 rounded-full text-sm font-bold"
          style={{ backgroundColor: statusConfig.color, color: COLORS.white }}
        >
          {statusConfig.label}
        </span>
      </div>

      {/* Items */}
      <div className="p-4 space-y-3">
        {order.items.map((item, i) => (
          <div key={i} className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span 
                  className="w-6 h-6 rounded-lg flex items-center justify-center text-sm font-bold"
                  style={{ backgroundColor: COLORS.orange, color: COLORS.white }}
                >
                  {item.quantity}
                </span>
                <span className="font-bold" style={{ color: COLORS.text }}>
                  {item.product_name}
                </span>
              </div>
              {/* Options */}
              {(item.size || item.milk || item.extras?.length > 0) && (
                <div className="ml-8 mt-1 space-y-0.5">
                  {item.size && (
                    <p className="text-xs" style={{ color: COLORS.textMuted }}>
                      Größe: {item.size === "small" ? "Klein" : item.size === "medium" ? "Mittel" : "Groß"}
                    </p>
                  )}
                  {item.milk && item.milk !== "regular" && (
                    <p className="text-xs" style={{ color: COLORS.textMuted }}>
                      Milch: {item.milk === "oat" ? "Hafer" : item.milk === "soy" ? "Soja" : item.milk === "almond" ? "Mandel" : "Kokos"}
                    </p>
                  )}
                  {item.extras?.length > 0 && (
                    <p className="text-xs" style={{ color: COLORS.textMuted }}>
                      + {item.extras.join(", ")}
                    </p>
                  )}
                </div>
              )}
            </div>
            <span className="font-bold" style={{ color: COLORS.green }}>
              {item.total_price.toFixed(2)}€
            </span>
          </div>
        ))}

        {/* Delivery Info */}
        {order.delivery_address && (
          <div 
            className="p-3 rounded-xl mt-3"
            style={{ backgroundColor: COLORS.bg }}
          >
            <div className="flex items-start gap-2">
              <MapPin size={16} style={{ color: COLORS.orange }} className="mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-bold" style={{ color: COLORS.text }}>Lieferung</p>
                <p style={{ color: COLORS.textMuted }}>{order.delivery_address}</p>
              </div>
            </div>
            {order.delivery_notes && (
              <p className="text-xs mt-2 italic" style={{ color: COLORS.textMuted }}>
                "{order.delivery_notes}"
              </p>
            )}
          </div>
        )}

        {/* Customer */}
        <div className="flex items-center gap-3 pt-2 border-t" style={{ borderColor: COLORS.bg }}>
          <User size={18} style={{ color: COLORS.textMuted }} />
          <span className="text-sm" style={{ color: COLORS.text }}>{order.user_name}</span>
          {order.user_phone && (
            <>
              <Phone size={14} style={{ color: COLORS.textMuted }} />
              <span className="text-sm" style={{ color: COLORS.textMuted }}>{order.user_phone}</span>
            </>
          )}
        </div>

        {/* Total */}
        <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: COLORS.bg }}>
          <span className="font-bold" style={{ color: COLORS.text }}>Gesamt</span>
          <span className="text-xl font-black" style={{ color: COLORS.green }}>
            {order.total.toFixed(2)}€
          </span>
        </div>

        {/* Zahlungsart - DEUTLICH ANZEIGEN */}
        <div 
          className="p-3 rounded-xl mt-2 flex items-center gap-3"
          style={{ 
            backgroundColor: order.payment_method === "cash" ? COLORS.red + "20" : COLORS.green + "15",
            border: order.payment_method === "cash" ? `2px solid ${COLORS.red}` : "none"
          }}
        >
          {order.payment_method === "cash" ? (
            <Money size={24} weight="fill" style={{ color: COLORS.red }} />
          ) : (
            <CreditCard size={24} weight="fill" style={{ color: COLORS.green }} />
          )}
          <div>
            <p className="font-black text-sm" style={{ color: order.payment_method === "cash" ? COLORS.red : COLORS.green }}>
              {order.payment_method === "cash" ? "💵 BARZAHLUNG" : 
               order.payment_method === "card" ? "💳 Kartenzahlung" : "💳 Online bezahlt"}
            </p>
            {order.payment_method === "cash" && (
              <p className="text-xs font-bold" style={{ color: COLORS.red }}>
                Fahrer kassiert {order.total.toFixed(2)}€
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="p-4 pt-0 space-y-2">
        {/* Time Selector for pending orders */}
        {order.status === "pending" && showTimeSelector && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            className="p-3 rounded-xl mb-2"
            style={{ backgroundColor: COLORS.bg }}
          >
            <p className="text-sm font-bold mb-2" style={{ color: COLORS.text }}>
              Geschätzte Zeit bis fertig:
            </p>
            <div className="flex gap-2">
              {[15, 20, 30, 45, 60].map((mins) => (
                <motion.button
                  key={mins}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setEstimatedTime(mins)}
                  className="flex-1 py-2 rounded-lg text-sm font-bold"
                  style={{
                    backgroundColor: estimatedTime === mins ? COLORS.green : COLORS.white,
                    color: estimatedTime === mins ? COLORS.white : COLORS.text
                  }}
                >
                  {mins}'
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {order.status === "pending" && (
          <div className="flex gap-2">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                if (!showTimeSelector) {
                  setShowTimeSelector(true);
                } else {
                  onAccept(order.id, estimatedTime);
                }
              }}
              className="flex-1 py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2"
              style={{ backgroundColor: COLORS.green }}
            >
              <Check size={20} weight="bold" />
              {showTimeSelector ? `Annehmen (${estimatedTime} Min.)` : "Annehmen"}
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => onCancel(order.id)}
              className="w-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: COLORS.red + "20" }}
            >
              <X size={20} weight="bold" style={{ color: COLORS.red }} />
            </motion.button>
          </div>
        )}

        {order.status === "confirmed" && (
          <div className="flex gap-2">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => onPreparing(order.id)}
              className="flex-1 py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2"
              style={{ backgroundColor: COLORS.tan }}
            >
              <CookingPot size={20} weight="fill" />
              Zubereitung starten
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => onPrint(order.id)}
              className="w-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: COLORS.bg }}
            >
              <Printer size={20} style={{ color: COLORS.textMuted }} />
            </motion.button>
          </div>
        )}

        {order.status === "preparing" && (
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => onReady(order.id)}
            className="w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2"
            style={{ backgroundColor: COLORS.green }}
          >
            <Package size={20} weight="fill" />
            Fertig - Bereit zur Abholung
          </motion.button>
        )}

        {order.status === "ready" && (
          <div 
            className="py-3 rounded-xl text-center font-bold"
            style={{ backgroundColor: COLORS.green + "15", color: COLORS.green }}
          >
            <Package size={20} weight="fill" className="inline mr-2" />
            Wartet auf Fahrer
          </div>
        )}

        {/* Estimated Time Display */}
        {order.estimated_minutes && order.status !== "ready" && (
          <div className="flex items-center justify-center gap-2 py-2">
            <Timer size={16} style={{ color: COLORS.orange }} />
            <span className="text-sm" style={{ color: COLORS.textMuted }}>
              Geschätzt: {order.estimated_minutes} Min.
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// RECEIPT MODAL WITH QR CODE FOR DRIVER
// ═══════════════════════════════════════════════════════════════════════════
const ReceiptModal = ({ receipt, isOpen, onClose }) => {
  const printRef = useRef(null);

  if (!isOpen || !receipt) return null;

  const handlePrint = () => {
    // Create a print-specific window
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Kundenbon #${receipt.header.order_number}</title>
        <style>
          body { 
            font-family: 'Courier New', monospace; 
            padding: 10px; 
            max-width: 80mm;
            margin: 0 auto;
            font-size: 12px;
          }
          .header { text-align: center; border-bottom: 2px dashed #ccc; padding-bottom: 10px; margin-bottom: 10px; }
          .header h1 { margin: 0; font-size: 18px; }
          .header .order-num { font-size: 20px; font-weight: bold; margin: 8px 0; }
          .customer { border-bottom: 2px dashed #ccc; padding-bottom: 10px; margin-bottom: 10px; }
          .items { border-bottom: 2px dashed #ccc; padding-bottom: 10px; margin-bottom: 10px; }
          .item { display: flex; justify-content: space-between; margin: 5px 0; }
          .item-options { font-size: 10px; color: #666; margin-left: 15px; }
          .totals .total { font-weight: bold; font-size: 16px; border-top: 1px solid #333; padding-top: 5px; }
          .notes { background: #f5f5f5; padding: 8px; margin-top: 10px; font-size: 10px; }
          .qr-section { 
            text-align: center; 
            margin-top: 15px; 
            padding-top: 15px; 
            border-top: 2px dashed #ccc;
          }
          .qr-section p { font-size: 10px; color: #666; margin-bottom: 5px; }
          .qr-code { margin: 10px auto; }
          .delivery-code { font-size: 14px; font-weight: bold; margin-top: 5px; }
          @media print {
            body { padding: 0; }
            @page { margin: 5mm; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>FRANKY'S FARM</h1>
          <p>${receipt.header.location}</p>
          <div class="order-num">#${receipt.header.order_number}</div>
          <p>${receipt.header.date} - ${receipt.header.time}</p>
        </div>
        
        <div class="customer">
          <strong>${receipt.customer.name}</strong><br>
          ${receipt.customer.phone ? receipt.customer.phone + '<br>' : ''}
          <span style="font-size:10px;color:#666;">${receipt.customer.address}</span>
        </div>
        
        <div class="items">
          ${receipt.items.map(item => `
            <div class="item">
              <span>${item.quantity}x ${item.name}</span>
              <span>${item.price.toFixed(2)}€</span>
            </div>
            ${item.options.length > 0 ? `<div class="item-options">${item.options.join(', ')}</div>` : ''}
          `).join('')}
        </div>
        
        <div class="totals">
          <div class="item">
            <span>Zwischensumme</span>
            <span>${receipt.totals.subtotal.toFixed(2)}€</span>
          </div>
          ${receipt.totals.delivery_fee > 0 ? `
            <div class="item">
              <span>Lieferung</span>
              <span>${receipt.totals.delivery_fee.toFixed(2)}€</span>
            </div>
          ` : ''}
          <div class="item total">
            <span>GESAMT</span>
            <span>${receipt.totals.total.toFixed(2)}€</span>
          </div>
        </div>
        
        ${receipt.payment_method ? `
          <div style="margin-top:15px;padding:10px;border-radius:8px;text-align:center;font-weight:bold;${
            receipt.payment_method === 'cash' 
              ? 'background:#DC354520;border:2px solid #DC3545;color:#DC3545;' 
              : 'background:#2D5A3D20;color:#2D5A3D;'
          }">
            ${receipt.payment_method === 'cash' 
              ? '💵 BARZAHLUNG - Fahrer kassiert ' + receipt.totals.total.toFixed(2) + '€' 
              : receipt.payment_method === 'card' 
                ? '💳 Kartenzahlung bei Lieferung' 
                : '✓ Online bereits bezahlt'}
          </div>
        ` : ''}
        
        ${receipt.notes ? `<div class="notes">Anmerkung: ${receipt.notes}</div>` : ''}
        
        ${receipt.estimated_time ? `<p style="text-align:center;font-weight:bold;margin-top:10px;">Fertig in ca. ${receipt.estimated_time} Min.</p>` : ''}
        
        ${receipt.qr_content ? `
          <div class="qr-section">
            <p>Fahrer QR-Code scannen:</p>
            <div class="qr-code">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(receipt.qr_content)}" alt="QR Code" />
            </div>
            <div class="delivery-code">${receipt.delivery_code}</div>
          </div>
        ` : ''}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Give some time for images to load
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-sm rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: COLORS.white }}
        onClick={e => e.stopPropagation()}
      >
        {/* Receipt Content - Print-friendly */}
        <div className="p-6 font-mono text-sm" ref={printRef} id="receipt-content">
          {/* Header */}
          <div className="text-center border-b-2 border-dashed pb-4 mb-4" style={{ borderColor: COLORS.bg }}>
            <h2 className="text-xl font-black">FRANKY'S FARM</h2>
            <p className="text-sm" style={{ color: COLORS.textMuted }}>
              {receipt.header.location}
            </p>
            <p className="mt-2 font-bold text-lg">#{receipt.header.order_number}</p>
            <p className="text-xs" style={{ color: COLORS.textMuted }}>
              {receipt.header.date} - {receipt.header.time}
            </p>
          </div>

          {/* Customer */}
          <div className="border-b-2 border-dashed pb-4 mb-4" style={{ borderColor: COLORS.bg }}>
            <p className="font-bold">{receipt.customer.name}</p>
            {receipt.customer.phone && <p>{receipt.customer.phone}</p>}
            <p className="text-xs mt-1" style={{ color: COLORS.textMuted }}>
              {receipt.customer.address}
            </p>
          </div>

          {/* Items */}
          <div className="space-y-3 border-b-2 border-dashed pb-4 mb-4" style={{ borderColor: COLORS.bg }}>
            {receipt.items.map((item, i) => (
              <div key={i}>
                <div className="flex justify-between">
                  <span>{item.quantity}x {item.name}</span>
                  <span>{item.price.toFixed(2)}€</span>
                </div>
                {item.options.length > 0 && (
                  <p className="text-xs ml-4" style={{ color: COLORS.textMuted }}>
                    {item.options.join(", ")}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Zwischensumme</span>
              <span>{receipt.totals.subtotal.toFixed(2)}€</span>
            </div>
            {receipt.totals.delivery_fee > 0 && (
              <div className="flex justify-between">
                <span>Lieferung</span>
                <span>{receipt.totals.delivery_fee.toFixed(2)}€</span>
              </div>
            )}
            <div className="flex justify-between font-black text-lg pt-2 border-t" style={{ borderColor: COLORS.bg }}>
              <span>GESAMT</span>
              <span>{receipt.totals.total.toFixed(2)}€</span>
            </div>
          </div>

          {/* Zahlungsart - DEUTLICH ANGEZEIGT */}
          {receipt.payment_method && (
            <div 
              className="mt-4 p-3 rounded-xl text-center font-bold"
              style={{ 
                backgroundColor: receipt.payment_method === "cash" ? COLORS.red + "20" : COLORS.green + "15",
                border: receipt.payment_method === "cash" ? `2px solid ${COLORS.red}` : "none",
                color: receipt.payment_method === "cash" ? COLORS.red : COLORS.green
              }}
            >
              {receipt.payment_method === "cash" 
                ? `💵 BARZAHLUNG - ${receipt.totals.total.toFixed(2)}€ kassieren` 
                : receipt.payment_method === "card" 
                  ? "💳 Kartenzahlung bei Lieferung" 
                  : "✓ Online bereits bezahlt"}
            </div>
          )}

          {/* Notes */}
          {receipt.notes && (
            <div className="mt-4 p-2 rounded" style={{ backgroundColor: COLORS.bg }}>
              <p className="text-xs">Anmerkung: {receipt.notes}</p>
            </div>
          )}

          {/* Time */}
          {receipt.estimated_time && (
            <p className="text-center mt-4 font-bold">
              Fertig in ca. {receipt.estimated_time} Min.
            </p>
          )}

          {/* QR Code for Driver */}
          {receipt.qr_content && (
            <div className="mt-6 pt-4 border-t-2 border-dashed text-center" style={{ borderColor: COLORS.bg }}>
              <div className="flex items-center justify-center gap-2 mb-3">
                <QrCode size={18} style={{ color: COLORS.orange }} />
                <span className="text-xs font-bold" style={{ color: COLORS.textMuted }}>
                  Fahrer scannt diesen Code
                </span>
              </div>
              <div className="flex justify-center">
                <div className="p-3 rounded-xl" style={{ backgroundColor: COLORS.white, border: `2px solid ${COLORS.bg}` }}>
                  <QRCodeSVG
                    value={receipt.qr_content}
                    size={100}
                    level="H"
                    includeMargin={false}
                  />
                </div>
              </div>
              <p className="mt-2 font-mono text-sm font-bold" style={{ color: COLORS.green }}>
                {receipt.delivery_code}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t flex gap-2" style={{ borderColor: COLORS.bg }}>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handlePrint}
            className="flex-1 py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2"
            style={{ backgroundColor: COLORS.green }}
            data-testid="print-receipt-btn"
          >
            <Printer size={20} />
            Drucken
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
            className="px-6 py-3 rounded-xl font-bold"
            style={{ backgroundColor: COLORS.bg, color: COLORS.textMuted }}
            data-testid="close-receipt-btn"
          >
            Schließen
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN KITCHEN PAGE
// ═══════════════════════════════════════════════════════════════════════════

// Sound für neue Bestellungen - laut und wiederholt
const NEW_ORDER_SOUND_URL = "https://cdn.pixabay.com/audio/2022/03/15/audio_b8fd50eb38.mp3"; // Ding-Sound

export default function KitchenPage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ pending: 0, preparing: 0, ready: 0, delivered_today: 0 });
  const [loading, setLoading] = useState(true);
  const [receipt, setReceipt] = useState(null);
  const [filter, setFilter] = useState("all"); // all, pending, preparing, ready
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [autoPrint, setAutoPrint] = useState(true);
  const audioRef = useRef(null);
  const lastPendingCountRef = useRef(0);
  const soundIntervalRef = useRef(null);

  const location = user?.location || "aachen";

  // Sound abspielen für neue Bestellungen
  const playNewOrderSound = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.volume = 1.0;
        audioRef.current.play().catch(e => console.log("Audio play prevented:", e));
      }
    } catch (err) {
      console.log("Sound error:", err);
    }
  }, [soundEnabled]);

  // Wiederholter Sound wenn Bestellungen warten
  useEffect(() => {
    // Sound-Element erstellen
    if (!audioRef.current) {
      audioRef.current = new Audio(NEW_ORDER_SOUND_URL);
      audioRef.current.volume = 1.0;
    }

    // Wenn pending > 0, alle 10 Sekunden Sound abspielen
    if (stats.pending > 0 && soundEnabled) {
      // Sofort Sound abspielen wenn neue Bestellung
      if (stats.pending > lastPendingCountRef.current) {
        playNewOrderSound();
        toast("🔔 Neue Bestellung eingegangen!", {
          style: { background: COLORS.orange, color: "white", fontWeight: "bold" }
        });
      }

      // Interval für wiederkehrenden Sound
      soundIntervalRef.current = setInterval(() => {
        if (soundEnabled) {
          playNewOrderSound();
        }
      }, 10000); // Alle 10 Sekunden
    } else {
      // Sound stoppen wenn keine pending Bestellungen
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
        soundIntervalRef.current = null;
      }
    }

    lastPendingCountRef.current = stats.pending;

    return () => {
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
      }
    };
  }, [stats.pending, soundEnabled, playNewOrderSound]);

  const loadOrders = useCallback(async () => {
    if (!token) return;
    try {
      const [ordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/kitchen/orders?location=${location}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/kitchen/stats?location=${location}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Load error:", err);
    }
    setLoading(false);
  }, [token, location]);

  useEffect(() => {
    loadOrders();
    // Poll every 10 seconds for new orders
    const interval = setInterval(loadOrders, 10000);
    return () => clearInterval(interval);
  }, [loadOrders]);

  // Check access
  if (!user || !["staff", "master-admin", "kitchen"].includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: COLORS.bg }}>
        <div className="text-center">
          <CookingPot size={64} weight="light" style={{ color: COLORS.textMuted }} className="mx-auto mb-4" />
          <h2 className="text-xl font-bold" style={{ color: COLORS.text }}>Küchen-Zugang erforderlich</h2>
          <p className="text-sm mt-2" style={{ color: COLORS.textMuted }}>
            Bitte melde dich mit einem Küchen-Account an.
          </p>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/landing")}
            className="mt-4 px-6 py-3 rounded-xl font-bold"
            style={{ backgroundColor: COLORS.green, color: COLORS.white }}
          >
            Zur Anmeldung
          </motion.button>
        </div>
      </div>
    );
  }

  const handleAccept = async (orderId, estimatedMinutes) => {
    try {
      await axios.post(
        `${API}/api/kitchen/orders/${orderId}/accept?estimated_minutes=${estimatedMinutes}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Bestellung angenommen - ${estimatedMinutes} Min.`);
      loadOrders();
      // Auto-print receipt if enabled
      if (autoPrint) {
        handlePrint(orderId);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handlePreparing = async (orderId) => {
    try {
      await axios.post(
        `${API}/api/kitchen/orders/${orderId}/preparing`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Zubereitung gestartet");
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleReady = async (orderId) => {
    try {
      await axios.post(
        `${API}/api/kitchen/orders/${orderId}/ready`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Bestellung fertig - Fahrer können annehmen");
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm("Bestellung wirklich stornieren?")) return;
    try {
      await axios.post(
        `${API}/api/kitchen/orders/${orderId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Bestellung storniert");
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handlePrint = async (orderId) => {
    try {
      const res = await axios.get(
        `${API}/api/orders/${orderId}/receipt`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReceipt(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden des Bons");
    }
  };

  const filteredOrders = filter === "all" 
    ? orders 
    : orders.filter(o => o.status === filter);

  return (
    <div className="min-h-screen pb-6" style={{ backgroundColor: COLORS.bg }}>
      {/* Header */}
      <header className="px-4 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => navigate("/landing")}
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: COLORS.white }}
            >
              <CaretLeft size={22} weight="bold" style={{ color: COLORS.text }} />
            </motion.button>
            <div>
              <h1 
                className="text-2xl font-black"
                style={{ color: COLORS.text, fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Küche
              </h1>
              <p className="text-sm" style={{ color: COLORS.textMuted }}>
                {location.charAt(0).toUpperCase() + location.slice(1)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ 
                backgroundColor: soundEnabled ? COLORS.green : COLORS.white,
              }}
              title={soundEnabled ? "Ton ausschalten" : "Ton einschalten"}
            >
              {soundEnabled ? (
                <SpeakerHigh size={20} weight="fill" className="text-white" />
              ) : (
                <SpeakerSlash size={20} weight="fill" style={{ color: COLORS.textMuted }} />
              )}
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => setShowSettings(!showSettings)}
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ 
                backgroundColor: showSettings ? COLORS.orange : COLORS.white,
              }}
              title="Drucker-Einstellungen"
            >
              <Gear size={20} weight={showSettings ? "fill" : "regular"} style={{ color: showSettings ? COLORS.white : COLORS.textMuted }} />
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={logout}
              className="px-4 py-2 rounded-xl text-sm font-bold"
              style={{ backgroundColor: COLORS.white, color: COLORS.textMuted }}
            >
              Abmelden
            </motion.button>
          </div>
        </div>
      </header>

      {/* Settings Panel */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-4 mb-4 overflow-hidden"
          >
            <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.white }}>
              <h3 className="font-bold mb-3 flex items-center gap-2" style={{ color: COLORS.text }}>
                <Gear size={18} />
                Drucker-Einstellungen
              </h3>
              
              {/* Auto-Print Toggle */}
              <div className="flex items-center justify-between p-3 rounded-xl mb-3" style={{ backgroundColor: COLORS.bg }}>
                <div>
                  <p className="font-bold text-sm" style={{ color: COLORS.text }}>Auto-Druck bei Annahme</p>
                  <p className="text-xs" style={{ color: COLORS.textMuted }}>
                    Bon automatisch drucken wenn Bestellung angenommen wird
                  </p>
                </div>
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setAutoPrint(!autoPrint)}
                  className="w-14 h-8 rounded-full p-1 transition-colors"
                  style={{ backgroundColor: autoPrint ? COLORS.green : COLORS.textMuted + "40" }}
                >
                  <motion.div
                    layout
                    className="w-6 h-6 rounded-full bg-white shadow"
                    style={{ marginLeft: autoPrint ? "auto" : 0 }}
                  />
                </motion.button>
              </div>

              {/* Printer Info */}
              <div className="p-3 rounded-xl" style={{ backgroundColor: COLORS.orange + "15" }}>
                <div className="flex items-center gap-2 mb-2">
                  <BluetoothConnected size={18} style={{ color: COLORS.orange }} />
                  <p className="text-sm font-bold" style={{ color: COLORS.orange }}>
                    Star Micronics Bluetooth
                  </p>
                </div>
                <p className="text-xs" style={{ color: COLORS.textMuted }}>
                  Drucker über System-Bluetooth koppeln. Beim Drucken im Browser-Dialog "Als Standard speichern" aktivieren.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats */}
      <div className="px-4 mb-4">
        <div className="grid grid-cols-4 gap-2">
          <div 
            className="p-3 rounded-xl text-center"
            style={{ backgroundColor: COLORS.orange + "20" }}
          >
            <p className="text-2xl font-black" style={{ color: COLORS.orange }}>
              {stats.pending}
            </p>
            <p className="text-xs" style={{ color: COLORS.textMuted }}>Neu</p>
          </div>
          <div 
            className="p-3 rounded-xl text-center"
            style={{ backgroundColor: COLORS.tan + "20" }}
          >
            <p className="text-2xl font-black" style={{ color: COLORS.tan }}>
              {stats.preparing}
            </p>
            <p className="text-xs" style={{ color: COLORS.textMuted }}>Zubereitung</p>
          </div>
          <div 
            className="p-3 rounded-xl text-center"
            style={{ backgroundColor: COLORS.green + "20" }}
          >
            <p className="text-2xl font-black" style={{ color: COLORS.green }}>
              {stats.ready}
            </p>
            <p className="text-xs" style={{ color: COLORS.textMuted }}>Fertig</p>
          </div>
          <div 
            className="p-3 rounded-xl text-center"
            style={{ backgroundColor: COLORS.white }}
          >
            <p className="text-2xl font-black" style={{ color: COLORS.text }}>
              {stats.delivered_today}
            </p>
            <p className="text-xs" style={{ color: COLORS.textMuted }}>Heute</p>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="px-4 mb-4">
        <div className="flex gap-2 p-1 rounded-xl" style={{ backgroundColor: COLORS.white }}>
          {[
            { id: "all", label: "Alle" },
            { id: "pending", label: "Neu" },
            { id: "preparing", label: "Zubereitung" },
            { id: "ready", label: "Fertig" }
          ].map((f) => (
            <motion.button
              key={f.id}
              whileTap={{ scale: 0.95 }}
              onClick={() => setFilter(f.id)}
              className="flex-1 py-2 rounded-lg font-bold text-sm"
              style={{
                backgroundColor: filter === f.id ? COLORS.green : "transparent",
                color: filter === f.id ? COLORS.white : COLORS.textMuted
              }}
            >
              {f.label}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Orders */}
      <div className="px-4 space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <CookingPot size={48} weight="light" style={{ color: COLORS.textMuted }} />
            </motion.div>
            <p className="mt-4" style={{ color: COLORS.textMuted }}>Lade Bestellungen...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12">
            <Coffee size={48} weight="light" style={{ color: COLORS.textMuted }} className="mx-auto" />
            <p className="mt-4 font-bold" style={{ color: COLORS.textMuted }}>
              {filter === "all" ? "Keine aktiven Bestellungen" : `Keine ${filter === "pending" ? "neuen" : filter === "preparing" ? "in Zubereitung" : "fertigen"} Bestellungen`}
            </p>
            <p className="text-sm" style={{ color: COLORS.textMuted }}>
              Neue Bestellungen erscheinen automatisch
            </p>
          </div>
        ) : (
          <AnimatePresence>
            {filteredOrders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onAccept={handleAccept}
                onPreparing={handlePreparing}
                onReady={handleReady}
                onCancel={handleCancel}
                onPrint={handlePrint}
              />
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Receipt Modal */}
      <AnimatePresence>
        {receipt && (
          <ReceiptModal 
            receipt={receipt} 
            isOpen={!!receipt} 
            onClose={() => setReceipt(null)} 
          />
        )}
      </AnimatePresence>

      {/* New Order Sound/Notification indicator */}
      {stats.pending > 0 && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 0.8, repeat: Infinity }}
          className="fixed bottom-6 right-6 w-16 h-16 rounded-full flex items-center justify-center shadow-2xl cursor-pointer"
          style={{ 
            backgroundColor: COLORS.red,
            boxShadow: `0 0 30px ${COLORS.red}80`
          }}
          onClick={() => {
            playNewOrderSound();
            setFilter("pending");
          }}
        >
          <Bell size={28} weight="fill" className="text-white" />
          <motion.span 
            className="absolute -top-2 -right-2 w-8 h-8 rounded-full text-sm font-black flex items-center justify-center"
            style={{ backgroundColor: COLORS.orange, color: COLORS.white }}
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 0.5, repeat: Infinity }}
          >
            {stats.pending}
          </motion.span>
        </motion.div>
      )}

      {/* Sound aktivieren Hinweis */}
      {!soundEnabled && stats.pending > 0 && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-6 left-4 right-20 p-3 rounded-xl flex items-center gap-3"
          style={{ backgroundColor: COLORS.orange }}
        >
          <SpeakerSlash size={24} weight="fill" className="text-white" />
          <div className="flex-1">
            <p className="text-white font-bold text-sm">Ton ist aus</p>
            <p className="text-white text-xs opacity-80">
              Klicke oben rechts um den Benachrichtigungston zu aktivieren
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
}
